import { create } from 'zustand'
import fetcher from "../fetcher";

// Monotonic counters to discard stale cascading-fetch responses. If the
// user flips Make twice quickly, the older /modelsForMake call can resolve
// after the newer one and overwrite the right answer with the wrong one;
// each fetch action stamps its in-flight request and bails on resolve if
// a newer request has since started.
let _makesSeq  = 0
let _modelsSeq = 0
let _trimsSeq  = 0

// Year is a path segment on `modelsForMake` / `trimsForModel`. Filters allow
// Year='All', which the endpoints take as the all-years sentinel.
const yearOrAll = (year) =>
    (year && year !== 'All') ? year : 'all'

// Cars endpoints return objects with either `cleaned_name` (makes,
// models) or `name` (trims). Fall back so all three use the same helper
// and the dropdown stays string-only.
const toOptionStrings = (rawList) =>
    (rawList || [])
        .map((item) => item?.cleaned_name || item?.name)
        .filter(Boolean)
        .sort()

// ─────────────────────────────────────────────────────────────────────────
// Filter state lives in the store; the URL is the source of truth for
// page reloads / shared links. `useFilterUrlSync` hydrates the store from
// the URL on mount and writes back on every change (debounced).
// ─────────────────────────────────────────────────────────────────────────

// Default value for every filter — also the "absence" value when writing
// to the URL (defaults are omitted from the query string).
const DEFAULT_FILTERS = {
    search:     '',
    status:     'All',
    dealType:   'All',
    year:       'All',
    make:       'All',
    model:      'All',
    trim:       'All',
    users:      'All',
    monthlyMin: '',
    monthlyMax: '',
    msrpMin:    '',
    msrpMax:    '',
}

// store-key ↔ URL-key map. Snake_case in URLs matches the backend's usual
// param style and stays readable when shared.
export const FILTER_URL_KEYS = {
    search:     's',
    status:     'status',
    dealType:   'type',
    year:       'year',
    make:       'make',
    model:      'model',
    trim:       'trim',
    users:      'users',
    monthlyMin: 'monthly_min',
    monthlyMax: 'monthly_max',
    msrpMin:    'msrp_min',
    msrpMax:    'msrp_max',
}

export const isDefaultFilterValue = (key, value) =>
    DEFAULT_FILTERS[key] === value

export const useListDealsStore = create((set, get) => ({
    deals: [],
    has_more: true,
    current_page: 0,
    count: 0,

    isLoading: false,
    setIsLoading: (value) => set({isLoading: value}),

    // ── View mode: 'grid' (card grid) | 'list' (table). Pure UI state. ──
    viewMode: 'grid',
    setViewMode: (value) => set({viewMode: value}),

    // ── Filter state (defaults; hydrated from URL by useFilterUrlSync) ──
    ...DEFAULT_FILTERS,

    // ── Cascading option lists for the Make / Model / Trim dropdowns ──
    // Always include 'All' as the first option so the dropdown has a way
    // back to "no filter" without us special-casing the UI.
    makeOptions:  ['All'],
    modelOptions: ['All'],
    trimOptions:  ['All'],

    isLoadingMakes:  false,
    isLoadingModels: false,
    isLoadingTrims:  false,

    setSearch: (value) => set({search: value}),
    setFilter: (key, value) => set({[key]: value}),

    // Used by the URL-sync hook: replace every filter at once so missing
    // keys reset to default rather than persisting stale values.
    hydrateFilters: (partial) => set({...DEFAULT_FILTERS, ...partial}),
    clearFilters: () => set(DEFAULT_FILTERS),

    // ── Cascading option fetches (mirrors fetcher.makesForYear /
    //    fetcher.modelsForMake / fetcher.trimsForModel). Each one:
    //      • bails when its parent filter is unset and resets the list
    //        back to ['All'] so the dropdown can't show stale options;
    //      • stamps a seq number so a late response from an older call
    //        can't overwrite a newer one;
    //      • collapses the raw [{cleaned_name, …}] response into the
    //        plain string[] shape the FilterDropdown consumes. ──
    fetchMakes: async (isUsed = false) => {
        const mySeq = ++_makesSeq
        set({isLoadingMakes: true})
        try {
            const response = await fetcher.makesForYear(isUsed)
            if (mySeq !== _makesSeq) return
            set({
                makeOptions:    ['All', ...toOptionStrings(response?.data)],
                isLoadingMakes: false,
            })
        } catch (_e) {
            if (mySeq !== _makesSeq) return
            set({isLoadingMakes: false})
        }
    },

    fetchModelsForMake: async (make, year, isUsed = false) => {
        // No parent → reset downstream and invalidate any in-flight call.
        if (!make || make === 'All') {
            ++_modelsSeq
            set({modelOptions: ['All'], isLoadingModels: false})
            return
        }
        const mySeq = ++_modelsSeq
        set({isLoadingModels: true})
        try {
            const response = await fetcher.modelsForMake(
                yearOrAll(year), make, isUsed
            )
            if (mySeq !== _modelsSeq) return
            set({
                modelOptions:    ['All', ...toOptionStrings(response?.data)],
                isLoadingModels: false,
            })
        } catch (_e) {
            if (mySeq !== _modelsSeq) return
            set({isLoadingModels: false})
        }
    },

    fetchTrimsForModel: async (make, model, year, isUsed = false) => {
        if (!make || make === 'All' || !model || model === 'All') {
            ++_trimsSeq
            set({trimOptions: ['All'], isLoadingTrims: false})
            return
        }
        const mySeq = ++_trimsSeq
        set({isLoadingTrims: true})
        try {
            const response = await fetcher.trimsForModel(
                yearOrAll(year), make, model, isUsed
            )
            if (mySeq !== _trimsSeq) return
            set({
                trimOptions:    ['All', ...toOptionStrings(response?.data)],
                isLoadingTrims: false,
            })
        } catch (_e) {
            if (mySeq !== _trimsSeq) return
            set({isLoadingTrims: false})
        }
    },

    fetchListDeals: async (params) => {
        set({isLoading: true})
        const response = await fetcher.getDeals(params)
        if (response?.data) {
            const { results, has_more, current_page, count } = response?.data

            if (current_page === 1) {
                set({
                    deals: results,
                    isLoading: false,
                    has_more: has_more,
                    current_page: current_page,
                    count: count
                })
            } else {
                const currentPage = get().current_page
                if (current_page !== currentPage) {
                    const currentList = get().deals

                    const newList = currentList.concat(results)
                    set({
                        deals: newList,
                        isLoading: false,
                        has_more: has_more,
                        current_page: current_page,
                        count: count
                    })
                }
            }
        }
    },

    shouldFetchNextDeals: () => {
        return !Boolean(get().isLoading) && Boolean(get().has_more)
    },

    removeDeletedDealFromList: (dealId) =>
        set((state) => ({
            deals: state.deals.filter((deal) => deal.id !== dealId),
        })),

    updateDealFromBackend: (newDeal) => {
        const newDeals = get().deals.map(deal =>
            deal.id === newDeal.id ? newDeal : deal
        );
        set({deals: newDeals})
    }
}))
